import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { AuditService } from '@/services/AuditService';
import {
  getMfaRequirement,
  getPreferredTotpFactor,
  MFA_SENSITIVE_ACTION_TTL_MS,
} from '@/lib/mfa';

type PerfilFormValues = {
  name: string;
  email: string;
  avatar_url: string;
};

interface ProfileEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ open, onOpenChange }) => {
  const { user, refreshProfile } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [mfaStatus, setMfaStatus] = useState<{
    enabled: boolean;
    factorId?: string;
    status?: string;
  } | null>(null);
  const [mfaEnrollData, setMfaEnrollData] = useState<{
    factorId: string;
    qr_code?: string;
    uri?: string;
  } | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [sensitiveAction, setSensitiveAction] = useState<'disable' | 'restart' | null>(null);
  const [sensitivePassword, setSensitivePassword] = useState('');
  const [sensitiveOtp, setSensitiveOtp] = useState('');
  const [sensitiveError, setSensitiveError] = useState<string | null>(null);
  const [sensitiveLoading, setSensitiveLoading] = useState(false);
  const [securityVerifiedUntil, setSecurityVerifiedUntil] = useState<number | null>(null);

  const form = useForm<PerfilFormValues>({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      avatar_url: user?.avatarUrl || '',
    },
  });

  useEffect(() => {
    form.reset({
      name: user?.name || '',
      email: user?.email || '',
      avatar_url: user?.avatarUrl || '',
    });
  }, [user]);

  const onSubmit = async (values: PerfilFormValues) => {
    if (!user) {
      toast({
        title: 'Usuário não autenticado',
        description: 'Faça login para editar seu perfil.',
        variant: 'destructive',
      });
      return;
    }

    let newAvatarPath: string | null = null;
    const bucket = 'imgsinsecrj';

    // Se houver uma nova foto pendente, faz o upload agora
    if (pendingAvatarFile) {
      setIsUploading(true);
      try {
        const ext = pendingAvatarFile.name.split('.').pop() || 'jpg';
        const path = `avatars/${user.id}/${Date.now()}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from(bucket)
          .upload(path, pendingAvatarFile, {
            upsert: false,
            contentType: pendingAvatarFile.type,
            cacheControl: '3600',
          });
        if (uploadErr) throw uploadErr;

        // Remover avatar anterior se existir e for do bucket
        const toStoragePath = (urlOrPath: string) => {
          const marker = '/storage/v1/object/public/imgsinsecrj/';
          const idx = urlOrPath.indexOf(marker);
          return idx >= 0 ? urlOrPath.substring(idx + marker.length) : urlOrPath;
        };
        const previousRaw = user.avatarUrl || '';
        const previousPath = toStoragePath(previousRaw);
        if (previousPath && previousPath.startsWith('avatars/') && previousPath !== path) {
          const { error: removeErr } = await supabase.storage.from(bucket).remove([previousPath]);
          if (removeErr) console.warn('Aviso ao remover avatar antigo:', removeErr.message);
        }

        // Cleanup de arquivos antigos no diretório do usuário
        try {
          const prefixDir = `avatars/${user.id}`;
          const { data: files, error: listErr } = await supabase.storage
            .from(bucket)
            .list(prefixDir);
          if (listErr) {
            console.warn('Aviso ao listar arquivos para cleanup:', listErr.message);
          } else if (Array.isArray(files)) {
            const stalePaths = files.map((f) => `${prefixDir}/${f.name}`).filter((p) => p !== path);
            if (stalePaths.length) {
              const { error: bulkRemoveErr } = await supabase.storage
                .from(bucket)
                .remove(stalePaths);
              if (bulkRemoveErr)
                console.warn('Aviso ao remover arquivos antigos:', bulkRemoveErr.message);
            }
          }
        } catch (cleanupErr: any) {
          console.warn('Aviso no cleanup de avatares:', cleanupErr?.message || cleanupErr);
        }

        newAvatarPath = path;
      } catch (err: any) {
        console.error('Erro no upload de avatar (ao salvar):', err?.message || err);
        toast({
          title: 'Erro ao enviar imagem',
          description: err?.message || 'Falha no upload.',
          variant: 'destructive',
        });
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const payload = {
      id: user.id,
      name: values.name?.trim() || null,
      email: user.email,
      avatar_url: newAvatarPath ?? (user.avatarUrl ? user.avatarUrl : null),
    };

    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();

    if (checkError) {
      console.warn('Aviso ao checar perfil (SELECT):', checkError.message);
    }

    let saveError: any = null;
    if (existingProfile?.id) {
      const { error: updateError } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', user.id);
      saveError = updateError || null;
    } else {
      const { error: insertError } = await supabase.from('profiles').insert(payload);
      saveError = insertError || null;
    }

    if (saveError) {
      console.error('Erro ao salvar perfil:', saveError);
      const isRLS =
        saveError.code === 'PGRST116' ||
        saveError.message?.toLowerCase().includes('row-level security') ||
        saveError.details?.toLowerCase().includes('rls');

      toast({
        title: 'Erro ao salvar perfil',
        description: isRLS
          ? 'Políticas RLS bloqueando a operação. Execute os scripts SQL no Supabase.'
          : saveError.message || 'Falha ao salvar suas alterações.',
        variant: 'destructive',
      });
      return;
    }

    // Alteração de senha (opcional) junto do salvar geral
    try {
      if (passwords.next) {
        if (passwords.next.length < 8) {
          throw new Error('A nova senha deve ter pelo menos 8 caracteres.');
        }
        if (passwords.next !== passwords.confirm) {
          throw new Error('Confirmação de senha não confere.');
        }
        if (passwords.current) {
          const signInCheck = await supabase.auth.signInWithPassword({
            email: user.email,
            password: passwords.current,
          });
          if (signInCheck.error) throw new Error('Senha atual incorreta.');
        }
        const { error: pwdErr } = await supabase.auth.updateUser({ password: passwords.next });
        if (pwdErr) throw pwdErr;
        setPasswords({ current: '', next: '', confirm: '' });
      }
    } catch (err: any) {
      toast({
        title: 'Erro ao alterar senha',
        description: err?.message || 'Falha ao atualizar.',
        variant: 'destructive',
      });
      return;
    }

    // Reset estados locais e atualizar perfil
    setPendingAvatarFile(null);
    setUploadPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await refreshProfile();
    toast({
      title: 'Perfil atualizado',
      description: 'Suas alterações foram salvas com sucesso.',
      variant: 'success',
    });
  };

  const avatarSrc = useMemo(() => {
    const bucket = 'imgsinsecrj';
    if (uploadPreview) return uploadPreview;
    const current = form.watch('avatar_url') || user?.avatarUrl;
    if (current) {
      if (/^https?:\/\//i.test(current)) return current;
      if (current.startsWith('avatars/')) {
        const { data } = supabase.storage.from(bucket).getPublicUrl(current);
        return data.publicUrl;
      }
    }
    return user?.email
      ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`
      : '/placeholder.svg';
  }, [uploadPreview, form, user]);

  const clearSensitiveActionState = () => {
    setSensitiveAction(null);
    setSensitivePassword('');
    setSensitiveOtp('');
    setSensitiveError(null);
    setSensitiveLoading(false);
  };

  const registerSecurityAudit = async (action: string, details: Record<string, unknown>) => {
    if (!user) return;
    try {
      await AuditService.logActivity(action, details, user.id);
    } catch (error) {
      console.warn('Falha ao registrar auditoria de segurança:', error);
    }
  };

  const requireRecentSecurityCheck = (action: 'disable' | 'restart') => {
    setSensitiveAction(action);
    setSensitiveError(null);
    setSensitiveOtp('');
    setSensitivePassword('');
  };

  const executeSensitiveAction = async (action: 'disable' | 'restart') => {
    if (action === 'disable') {
      if (!mfaStatus?.factorId) return;
      const { error } = await supabase.auth.mfa.unenroll({ factorId: mfaStatus.factorId });
      if (error) {
        toast({ title: 'Erro ao desativar 2FA', description: error.message, variant: 'destructive' });
        return;
      }

      await loadMfaStatus();
      clearSensitiveActionState();
      setSecurityVerifiedUntil(null);
      await registerSecurityAudit('security_mfa_disabled', {
        factorId: mfaStatus.factorId,
      });
      toast({
        title: '2FA desativado',
        description: 'Autenticação em duas etapas desativada.',
        variant: 'success',
      });
      return;
    }

    const factorId = mfaStatus?.factorId;
    if (factorId) {
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId });
      if (unenrollError) {
        throw unenrollError;
      }
    }

    const uniqueName = `Authenticator ${new Date().toISOString()}`;
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: uniqueName,
    });
    if (error) {
      throw error;
    }

    setMfaEnrollData({ factorId: data.id, qr_code: data.totp?.qr_code, uri: data.totp?.uri });
    await loadMfaStatus();
    clearSensitiveActionState();
    setSecurityVerifiedUntil(null);
    await registerSecurityAudit('security_mfa_restarted', {
      previousFactorId: factorId ?? null,
      newFactorId: data.id,
    });
    toast({ title: 'Novo QR Code gerado', description: 'Escaneie o novo QR no seu app.' });
  };

  const confirmSensitiveAction = async () => {
    if (!user || !sensitiveAction) return;

    if (!sensitivePassword) {
      setSensitiveError('Digite sua senha atual para continuar.');
      return;
    }

    setSensitiveLoading(true);
    setSensitiveError(null);

    try {
      const signInCheck = await supabase.auth.signInWithPassword({
        email: user.email,
        password: sensitivePassword,
      });

      if (signInCheck.error) {
        throw new Error('Senha atual incorreta.');
      }

      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const { hasVerifiedAssurance } = getMfaRequirement(aalData);

      if (!hasVerifiedAssurance && mfaStatus?.factorId) {
        const sanitizedOtp = sensitiveOtp.replace(/\D/g, '');
        if (sanitizedOtp.length !== 6) {
          throw new Error('Digite o código de 6 dígitos do autenticador para confirmar.');
        }

        const { error: challengeError } = await supabase.auth.mfa.challengeAndVerify({
          factorId: mfaStatus.factorId,
          code: sanitizedOtp,
        });

        if (challengeError) {
          throw new Error('Código do autenticador inválido.');
        }
      }

      setSecurityVerifiedUntil(Date.now() + MFA_SENSITIVE_ACTION_TTL_MS);
      await executeSensitiveAction(sensitiveAction);
    } catch (error: any) {
      const message = error?.message || 'Não foi possível validar sua identidade.';
      setSensitiveError(message);
      await registerSecurityAudit('security_mfa_sensitive_action_failed', {
        action: sensitiveAction,
        reason: message,
      });
    } finally {
      setSensitiveLoading(false);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    // Apenas pré-visualiza e marca para upload ao salvar
    const reader = new FileReader();
    reader.onload = (evt) => setUploadPreview((evt.target?.result as string) || null);
    reader.readAsDataURL(file);
    setPendingAvatarFile(file);
  };

  const loadMfaStatus = async () => {
    const factors = await supabase.auth.mfa.listFactors();
    if (factors.error) {
      console.warn('Erro ao listar fatores MFA:', factors.error.message);
      setMfaStatus({ enabled: false });
      return;
    }
    const totp = getPreferredTotpFactor(factors.data);
    setMfaStatus({ enabled: !!totp, factorId: totp?.id, status: (totp as any)?.status });
  };

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const startEnroll2FA = async () => {
    // Primeiro, verificar se já existe um fator TOTP
    const factors = await supabase.auth.mfa.listFactors();
    const existingTotp = getPreferredTotpFactor(factors.data);

    if (existingTotp) {
      // Se já estiver verificado, apenas informar ao usuário
      if ((existingTotp as any).status === 'verified') {
        setMfaStatus({ enabled: true, factorId: existingTotp.id, status: 'verified' });
        toast({
          title: '2FA já está ativo',
          description: 'Um fator TOTP já está configurado para sua conta.',
        });
        return;
      }

      // Se houver um fator pendente, permitir continuar a verificação
      setMfaEnrollData({ factorId: existingTotp.id });
      toast({
        title: '2FA pendente',
        description:
          'Finalize a verificação inserindo o código de 6 dígitos. Se precisar de um novo QR Code, reinicie o processo.',
      });
      return;
    }

    // Não existe fator: iniciar o enrollment com um nome amigável único
    const uniqueName = `Authenticator ${new Date().toISOString()}`;
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: uniqueName,
    });
    if (error) {
      toast({ title: 'Erro ao iniciar 2FA', description: error.message, variant: 'destructive' });
      return;
    }
    setMfaEnrollData({ factorId: data.id, qr_code: data.totp?.qr_code, uri: data.totp?.uri });
    toast({
      title: '2FA iniciado',
      description: 'Escaneie o QR Code no seu app autenticador.',
      variant: 'success',
    });
  };

  const verifyEnroll2FA = async () => {
    if (!mfaEnrollData?.factorId || !otpCode) return;
    // Sanitiza o código para evitar espaços ou caracteres não numéricos
    const sanitized = otpCode.replace(/\D/g, '');
    if (sanitized.length !== 6) {
      toast({
        title: 'Código inválido',
        description: 'Digite os 6 dígitos do app autenticador.',
        variant: 'destructive',
      });
      return;
    }
    // Para TOTP, crie um desafio e verifique imediatamente com o código
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: mfaEnrollData.factorId,
      code: sanitized,
    });
    if (error) {
      toast({ title: 'Código inválido', description: error.message, variant: 'destructive' });
      return;
    }
    setMfaEnrollData(null);
    setOtpCode('');
    await loadMfaStatus();
    toast({
      title: '2FA ativado',
      description: 'Seu 2FA via TOTP foi configurado.',
      variant: 'success',
    });
  };

  // Permite remover um fator pendente e reiniciar para gerar novo QR Code
  const restartEnroll2FA = async () => {
    try {
      const factors = await supabase.auth.mfa.listFactors();
      const existingTotp = getPreferredTotpFactor(factors.data);
      if (existingTotp) {
        await supabase.auth.mfa.unenroll({ factorId: existingTotp.id });
      }
      const uniqueName = `Authenticator ${new Date().toISOString()}`;
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: uniqueName,
      });
      if (error) throw error;
      setMfaEnrollData({ factorId: data.id, qr_code: data.totp?.qr_code, uri: data.totp?.uri });
      toast({ title: 'Novo QR Code gerado', description: 'Escaneie o novo QR no seu app.' });
    } catch (err: any) {
      toast({
        title: 'Erro ao reiniciar 2FA',
        description: err?.message || 'Falha ao reiniciar.',
        variant: 'destructive',
      });
    }
  };

  const disable2FA = async () => {
    if (!mfaStatus?.factorId) return;
    if (securityVerifiedUntil && securityVerifiedUntil > Date.now()) {
      await executeSensitiveAction('disable');
      return;
    }

    requireRecentSecurityCheck('disable');
  };

  const handleChangePassword = async () => {
    if (!user) return;
    if (!passwords.next || passwords.next.length < 8) {
      toast({
        title: 'Senha fraca',
        description: 'A nova senha deve ter pelo menos 8 caracteres.',
        variant: 'destructive',
      });
      return;
    }
    if (passwords.next !== passwords.confirm) {
      toast({
        title: 'Senhas diferentes',
        description: 'Confirmação não confere.',
        variant: 'destructive',
      });
      return;
    }
    setChangingPassword(true);
    try {
      if (passwords.current) {
        const signInCheck = await supabase.auth.signInWithPassword({
          email: user.email,
          password: passwords.current,
        });
        if (signInCheck.error) throw new Error('Senha atual incorreta.');
      }

      const { error } = await supabase.auth.updateUser({ password: passwords.next });
      if (error) throw error;
      setPasswords({ current: '', next: '', confirm: '' });
      toast({
        title: 'Senha alterada',
        description: 'Use a nova senha no próximo login.',
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Erro ao alterar senha',
        description: err.message || 'Falha ao atualizar.',
        variant: 'destructive',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] p-0">
        <div className="flex flex-col max-h-[90vh]">
          {/* Cabeçalho fixo no topo do modal */}
          <DialogHeader className="px-4 pt-4 pb-3 border-b bg-background">
            <DialogTitle>Editar perfil</DialogTitle>
            <DialogDescription>
              Atualize suas informações e gerencie a autenticação de dois fatores.
            </DialogDescription>
          </DialogHeader>

          {/* Área rolável apenas para o conteúdo */}
          <div className="overflow-y-auto">
            <div className="px-4 pt-5 pb-4 space-y-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="flex items-center">
                    <div
                      className="relative group inline-block rounded-full cursor-pointer"
                      onClick={handleSelectFile}
                      role="button"
                      aria-label="Alterar foto de perfil"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <img
                        src={avatarSrc}
                        alt={user?.name || 'Avatar'}
                        className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover border"
                      />
                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/25 transition-colors">
                        <div className="flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-black/30 group-hover:bg-black/40 backdrop-blur-[1.5px]">
                          <ImagePlus className="h-4 w-4 sm:h-5 sm:w-5 text-white opacity-80 group-hover:opacity-100" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome</FormLabel>
                        <FormControl>
                          <Input placeholder="Seu nome" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-mail</FormLabel>
                        <FormControl>
                          <Input disabled {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </form>
              </Form>

              <Separator />

              <div className="space-y-3">
                <div>
                  <h2 className="text-lg font-semibold">Alteração de senha</h2>
                  <p className="text-xs text-muted-foreground">
                    Por segurança, confirme sua senha atual antes de alterar.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Senha atual</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={passwords.current}
                      onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Nova senha</Label>
                    <Input
                      type="password"
                      placeholder="mín. 8 caracteres"
                      value={passwords.next}
                      onChange={(e) => setPasswords((p) => ({ ...p, next: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Confirmar nova senha</Label>
                    <Input
                      type="password"
                      placeholder="repita a senha"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
                    />
                  </div>
                </div>
                {/* Removido botão próprio de alteração; senha será aplicada no salvar geral */}
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">Autenticação em duas etapas (2FA)</h2>
                  <p className="text-xs text-muted-foreground">
                    Use um app autenticador (Google Authenticator, Authy, etc.).
                  </p>
                </div>

                {!mfaStatus?.enabled && !mfaEnrollData && (
                  <div className="flex justify-start">
                    <Button type="button" variant="secondary" onClick={startEnroll2FA}>
                      Ativar 2FA
                    </Button>
                  </div>
                )}

                {mfaEnrollData && (
                  <div className="grid grid-cols-1 sm:grid-cols-[220px,1fr] gap-6 items-start">
                    <div className="rounded-md border p-3 flex items-center justify-center bg-muted/20">
                      {mfaEnrollData.qr_code ? (
                        <img src={mfaEnrollData.qr_code} alt="QR Code 2FA" className="h-44 w-44" />
                      ) : (
                        <div className="text-xs space-y-2">
                          <div>Fator TOTP existente pendente de verificação.</div>
                          <div>
                            Se você já configurou o app, digite o código abaixo. Caso precise de um
                            novo QR Code, clique em "Reiniciar".
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Label>Digite o código de 6 dígitos do app</Label>
                      <InputOTP maxLength={6} value={otpCode} onChange={(val) => setOtpCode(val)}>
                        <InputOTPGroup>
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <InputOTPSlot key={i} index={i} />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                      <div className="flex gap-2">
                        <Button type="button" variant="success" onClick={verifyEnroll2FA}>
                          Confirmar
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setMfaEnrollData(null);
                            setOtpCode('');
                          }}
                        >
                          Cancelar
                        </Button>
                        {!mfaEnrollData.qr_code && (
                          <Button type="button" variant="secondary" onClick={restartEnroll2FA}>
                            Reiniciar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {mfaStatus?.enabled && !mfaEnrollData && (
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <div className="font-medium">2FA está ativado</div>
                      <div className="text-xs text-muted-foreground">
                        Fator TOTP configurado. Você pode desativar se necessário.
                      </div>
                    </div>
                    <Button type="button" variant="destructive" onClick={disable2FA}>
                      Desativar 2FA
                    </Button>
                  </div>
                )}
                {mfaStatus?.enabled && !mfaEnrollData && (
                  <div className="space-y-3 rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">
                      Para trocar ou desativar o autenticador com seguranÃ§a, confirme novamente sua identidade.
                    </p>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          if (securityVerifiedUntil && securityVerifiedUntil > Date.now()) {
                            executeSensitiveAction('restart').catch((error: any) => {
                              toast({
                                title: 'Erro ao trocar autenticador',
                                description: error?.message || 'Falha ao reiniciar 2FA.',
                                variant: 'destructive',
                              });
                            });
                            return;
                          }

                          requireRecentSecurityCheck('restart');
                        }}
                      >
                        Trocar autenticador
                      </Button>
                    </div>

                    {sensitiveAction && (
                      <div className="space-y-3 rounded-md border bg-muted/20 p-4">
                        <Alert>
                          <AlertTitle>Confirmação de segurança</AlertTitle>
                          <AlertDescription>
                            Digite sua senha atual e, se solicitado, o código do autenticador para confirmar esta alteração sensível.
                          </AlertDescription>
                        </Alert>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Senha atual</Label>
                            <Input
                              type="password"
                              placeholder="Digite sua senha"
                              value={sensitivePassword}
                              onChange={(e) => setSensitivePassword(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Código do autenticador</Label>
                            <InputOTP maxLength={6} value={sensitiveOtp} onChange={setSensitiveOtp}>
                              <InputOTPGroup>
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                  <InputOTPSlot key={i} index={i} />
                                ))}
                              </InputOTPGroup>
                            </InputOTP>
                          </div>
                        </div>

                        <p className="text-xs text-muted-foreground">
                          Se sua sessÃ£o jÃ¡ estiver em AAL2, o cÃ³digo pode nÃ£o ser exigido. Sem o autenticador, a recuperaÃ§Ã£o deve ser feita com suporte administrativo.
                        </p>

                        {sensitiveError && <p className="text-sm text-destructive">{sensitiveError}</p>}

                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="success" onClick={confirmSensitiveAction} disabled={sensitiveLoading}>
                            {sensitiveLoading ? 'Validando...' : 'Confirmar'}
                          </Button>
                          <Button type="button" variant="outline" onClick={clearSensitiveActionState} disabled={sensitiveLoading}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Rodapé fixo na base do modal */}
          <div className="border-t bg-background px-4 py-3">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  // Reverte alterações locais e fecha
                  form.reset({
                    name: user?.name || '',
                    email: user?.email || '',
                    avatar_url: user?.avatarUrl || '',
                  });
                  setPendingAvatarFile(null);
                  setUploadPreview(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                  onOpenChange(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="success"
                onClick={form.handleSubmit(onSubmit)}
                disabled={isUploading}
              >
                Salvar alterações
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileEditModal;
